export type FamilyAccessTier =
  | 'UNSPONSORED_FAMILY'
  | 'SPONSORED_MEMBER'
  | 'MEMBERSHIP_OWNER';

export type FamilyCapability =
  | 'VIEW_FAMILY_TREE'
  | 'CLAIM_OWN_PROFILE'
  | 'EDIT_OWN_PROFILE'
  | 'UPLOAD_PROFILE_IMAGE'
  | 'UPLOAD_LIMITED_PHOTOS'
  | 'UPLOAD_UNLIMITED_MEDIA'
  | 'USE_CALLS'
  | 'USE_VIDEO_CALLS'
  | 'USE_PREMIUM_ARCHIVE'
  | 'EDIT_ANOTHER_CLAIMED_PROFILE'
  | 'EDIT_ANOTHER_MEMBERS_MEDIA';

export interface UnsponsoredFamilyAccessPolicy {
  tier: 'UNSPONSORED_FAMILY';
  status: 'ACTIVE';
  familyIdentityPermanent: true;
  membershipRequiredForFamilyIdentity: false;

  mayJoinFamilyTree: true;
  mayClaimOwnProfile: true;
  mayCreateOwnProfile: true;
  mayEditOwnProfile: true;

  editableOwnProfileFields: readonly [
    'name',
    'date_of_birth',
    'nickname',
    'profile_image'
  ];

  photoLimit: 5;
  profileImageCountsTowardPhotoLimit: true;

  mayUploadPhotos: true;
  mayUploadVideos: false;
  mayUseCalls: false;
  mayUseVideoCalls: false;
  mayUsePremiumArchive: false;

  mayEditAnotherClaimedProfile: false;
  mayEditAnotherMembersMedia: false;

  mayPurchaseSeparateMembership: true;
  membershipUpgradePreservesFamilyBinding: true;

  constitutionalRule: string;
  nextMilestone: string;
}

export interface FamilyCapabilityDecision {
  tier: FamilyAccessTier;
  capability: FamilyCapability;
  allowed: boolean;
  reason:
    | 'FAMILY_IDENTITY_ALLOWED'
    | 'OWN_PROFILE_AUTHORITY'
    | 'LIMITED_PHOTO_AUTHORITY'
    | 'PAID_CAPABILITY_REQUIRED'
    | 'CLAIMED_MEMBER_OWNERSHIP_REQUIRED'
    | 'MEMBERSHIP_CAPABILITY_ALLOWED';
}

export function projectUnsponsoredFamilyAccessPolicy():
  UnsponsoredFamilyAccessPolicy {
  return Object.freeze({
    tier: 'UNSPONSORED_FAMILY',
    status: 'ACTIVE',

    familyIdentityPermanent: true,
    membershipRequiredForFamilyIdentity: false,

    mayJoinFamilyTree: true,
    mayClaimOwnProfile: true,
    mayCreateOwnProfile: true,
    mayEditOwnProfile: true,

    editableOwnProfileFields: [
      'name',
      'date_of_birth',
      'nickname',
      'profile_image',
    ],

    photoLimit: 5,
    profileImageCountsTowardPhotoLimit: true,

    mayUploadPhotos: true,
    mayUploadVideos: false,
    mayUseCalls: false,
    mayUseVideoCalls: false,
    mayUsePremiumArchive: false,

    mayEditAnotherClaimedProfile: false,
    mayEditAnotherMembersMedia: false,

    mayPurchaseSeparateMembership: true,
    membershipUpgradePreservesFamilyBinding: true,

    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextMilestone:
      'MAE-003B — Unsponsored Family Capability Enforcement',
  });
}

export function decideFamilyCapability(
  tier: FamilyAccessTier,
  capability: FamilyCapability,
): FamilyCapabilityDecision {
  if (
    capability === 'VIEW_FAMILY_TREE' ||
    capability === 'CLAIM_OWN_PROFILE'
  ) {
    return {
      tier,
      capability,
      allowed: true,
      reason: 'FAMILY_IDENTITY_ALLOWED',
    };
  }

  if (capability === 'EDIT_OWN_PROFILE') {
    return {
      tier,
      capability,
      allowed: true,
      reason: 'OWN_PROFILE_AUTHORITY',
    };
  }

  if (
    tier === 'UNSPONSORED_FAMILY' &&
    (
      capability === 'UPLOAD_PROFILE_IMAGE' ||
      capability === 'UPLOAD_LIMITED_PHOTOS'
    )
  ) {
    return {
      tier,
      capability,
      allowed: true,
      reason: 'LIMITED_PHOTO_AUTHORITY',
    };
  }

  if (
    capability === 'EDIT_ANOTHER_CLAIMED_PROFILE' ||
    capability === 'EDIT_ANOTHER_MEMBERS_MEDIA'
  ) {
    return {
      tier,
      capability,
      allowed: false,
      reason: 'CLAIMED_MEMBER_OWNERSHIP_REQUIRED',
    };
  }

  if (tier === 'UNSPONSORED_FAMILY') {
    return {
      tier,
      capability,
      allowed: false,
      reason: 'PAID_CAPABILITY_REQUIRED',
    };
  }

  return {
    tier,
    capability,
    allowed: true,
    reason: 'MEMBERSHIP_CAPABILITY_ALLOWED',
  };
}

export const UNSPONSORED_FAMILY_ACCESS_AUTHORITY_READY =
  true;
